import React, { useEffect, useState } from 'react';
import { Capacitor } from '@capacitor/core';

import { Loader } from '@/components/shared/Loader';
import { LinkFrom } from '@/config/constants';
import { dashboardUrl, libraryUrl, videoUrl } from '@/config/urls';
import {
	GetReelFeedQuery,
	useGetReelFeedQuery,
	useUpsertReelStatMutation,
} from '@/graphql';
import applyParams from '@/helpers/applyParams';
import { EDarkModeSettings, useDarkMode } from '@/hooks/useDarkMode';
import { useFormattedTitle } from '@/hooks/useFormattedTitle';
import { useStatusBar } from '@/hooks/useStatusBar';
import {
	useAnalyticsEvent,
	useFireAnalyticsPageView,
} from '@/providers/AnalyticsProvider';
import constants from '@/theme/exportedVariables.module.scss';

import useMediaQuery from '@material-ui/core/useMediaQuery/useMediaQuery';
import { useSnackbar } from 'notistack';
import { Helmet } from 'react-helmet';
import { useLocation, useNavigate } from 'react-router';
import { useSwipeable } from 'react-swipeable';
import useLocalStorage from 'react-use/lib/useLocalStorage';

import { Player } from './components/Player/Player';
import { SwipeUpScreen } from './components/SwipeUpScreen';
import styles from './Reels.module.scss';

const isCapacitor = Capacitor.isNativePlatform();

interface ReelsLocationState {
	linkFrom: LinkFrom;
}

const ACTIVE_VIDEO_PLAYERS_BEFORE = 1;
const ACTIVE_VIDEO_PLAYERS_AHEAD = 1;
const DEBUG = false;

export const Reels: React.FC = () => {
	const { enqueueSnackbar } = useSnackbar();
	const navigate = useNavigate();
	const location = useLocation();
	const [index, setIndex] = useState(0);
	const [title] = useFormattedTitle('Asaph | Reels');
	const { currentMode, setStatusBarColor } = useStatusBar();
	const { handleModeChange, getDefaultDarkMode } = useDarkMode();
	const [muted, setMuted] = useState(false);
	const [reels, setReels] = useState<GetReelFeedQuery['getReelFeed']>([]);
	const fireReachedEndEvent = useAnalyticsEvent('Reels_Reached_End');
	const fireReactionEvent = useAnalyticsEvent('Reels_Reel_Reaction');
	const fireErrorEvent = useAnalyticsEvent('Error');
	const fireUnmuteEvent = useAnalyticsEvent('Reels_Unmute');
	const [localStorageToken, setLocalStorageToken] = useLocalStorage<
		boolean | null
	>('reelsSwipeUpSeen', false);
	const isDesktop = useMediaQuery(
		`(min-width: ${constants.sizeSmallDesktop}`,
	);

	const { mutateAsync } = useUpsertReelStatMutation();
	const { data, isLoading } = useGetReelFeedQuery(
		{},
		{ refetchOnWindowFocus: false, refetchOnMount: false },
	);

	useEffect(() => {
		if (data?.getReelFeed) {
			setReels(data.getReelFeed);
		}
	}, [data?.getReelFeed]);

	useEffect(() => {
		handleModeChange({
			mode: EDarkModeSettings.ON,
		});

		return () =>
			handleModeChange({
				mode: getDefaultDarkMode(),
			});
	});

	useEffect(() => {
		setStatusBarColor(constants.colorLight0, constants.colorDark0);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentMode]);

	useFireAnalyticsPageView({
		enabled: !!title,
		pageViewArgs: {
			title,
		},
	});

	const handlers = useSwipeable({
		onSwipedUp: (eventData) => {
			setLocalStorageToken(true);
			setIndex((currentIndex) => {
				const nextIndex = currentIndex + 1;
				if (nextIndex > (data?.getReelFeed || []).length - 1) {
					enqueueSnackbar(
						'You have reached the end of the reel feed.',
					);
					fireReachedEndEvent();
					return currentIndex;
				}
				return nextIndex;
			});
		},
		onSwipedDown: (eventData) =>
			setIndex((currentIndex) => {
				const nextIndex = currentIndex - 1;
				return nextIndex < 0 ? 0 : nextIndex;
			}),
	});

	if (isLoading || !data) {
		return <Loader />;
	}
	const locationState = location.state as ReelsLocationState;

	const hasMore = index < data.getReelFeed.length - 1;
	const chunkStart =
		index - ACTIVE_VIDEO_PLAYERS_BEFORE < 0
			? 0
			: index - ACTIVE_VIDEO_PLAYERS_BEFORE;
	const chunkEnd =
		index + ACTIVE_VIDEO_PLAYERS_AHEAD > reels.length - 1
			? reels.length - 1
			: index + ACTIVE_VIDEO_PLAYERS_AHEAD;

	const reelsClasses = [styles['reels'], DEBUG && styles['debug']]
		.filter(Boolean)
		.join(' ');

	return (
		<>
			<Helmet title={title}>
				<style type="text/css">{`
          body .ion-page {
            top: 0;
          }
        `}</style>
			</Helmet>
			<div className={reelsClasses} {...handlers}>
				<div
					className={styles['container']}
					style={
						DEBUG
							? { overflow: 'auto' }
							: { transform: `translateY(-${index * 100}%)` }
					}
				>
					{!localStorageToken && !isDesktop && (
						<SwipeUpScreen
							onClick={() => setLocalStorageToken(true)}
						/>
					)}
					{reels.map((reel, reelIndex) => {
						const reelInChunk =
							chunkStart <= reelIndex && reelIndex <= chunkEnd;

						const reelClasses = [
							styles['reel'],
							index === reelIndex && styles['active'],
							reelInChunk && styles['preloading'],
						]
							.filter(Boolean)
							.join(' ');

						return reelInChunk ? (
							<Player
								key={`reel-${reel.id}`}
								className={reelClasses}
								videoId={reel.id}
								name={reel.name}
								playing={index === reelIndex}
								preload={reelInChunk}
								muted={muted}
								posterUrl={reel.posterUrl}
								url={reel.url}
								setMuted={(state) => {
									setMuted(state);
									if (!state) {
										fireUnmuteEvent();
									}
								}}
								description={reel.video?.name}
								contributor={{
									name: reel.video?.contributor?.name,
									imageUrl: reel.video?.contributor?.imageUrl,
								}}
								videoLink={applyParams(videoUrl, {
									token: reel.videoId,
								})}
								videoName={reel.video?.name}
								liked={!!reel.stat?.liked}
								onLikeClick={(state) => {
									mutateAsync({
										reelId: reel.id,
										input: {
											liked: state,
										},
									})
										.then((res) => {
											fireReactionEvent({ liked: state });
										})
										.catch((e) => {
											console.warn(e);
											fireErrorEvent({
												name: 'ReelReactionError',
												details: e,
											});
											enqueueSnackbar(
												'Your reel reaction failed to be processed',
												{
													variant: 'warning',
												},
											);
											// If there was an error recording the reaction
											// then revert to previous value after error is logged
											setReels(
												reels.map((r, ri) => {
													if (ri === reelIndex) {
														r.stat = {
															liked: !state,
														};
													}
													return r;
												}),
											);
										});
									// Record user feedback immediately
									setReels(
										reels.map((r, ri) => {
											if (ri === reelIndex) {
												r.stat = {
													liked: state,
												};
											}
											return r;
										}),
									);
								}}
							/>
						) : (
							<div className={styles['reel']} />
						);
					})}
				</div>

				<div className={styles['gradient']}>
					<div className={styles['navigation']}>
						<div className={styles['left']}>
							<img
								src="/assets/back-moderat-white.svg"
								className={styles['close']}
								onClick={() => {
									if (
										locationState?.linkFrom ===
										LinkFrom.library
									) {
										return navigate(libraryUrl);
									}
									navigate(dashboardUrl);
								}}
							/>
						</div>
						<div className={styles['center']}>Reels</div>
						<div className={styles['right']}>
							<div
								onClick={() => setMuted(!muted)}
								className={`${styles['button']} ${styles['sound']}`}
							>
								{muted ? 'Unmute' : 'Mute'}
							</div>
						</div>
					</div>
				</div>

				{index > 0 && (
					<div
						className={styles['top']}
						onClick={() => setIndex((index) => index - 1)}
					>
						<img src="/assets/back-moderat.svg" />
					</div>
				)}
				{hasMore && (
					<div
						className={styles['bottom']}
						onClick={() => setIndex((index) => index + 1)}
					>
						<img src="/assets/back-moderat.svg" />
					</div>
				)}

				{/* <Button
						variant="contained"
						onClick={() => setIndex((index) => index - 1)}
					>
						Prev
					</Button>
					<Button
						variant="contained"
						onClick={() => setIndex((index) => index + 1)}
					>
						Next
					</Button> */}
			</div>
		</>
	);
};
